import { Localized } from "@fluent/react";
import { Autocomplete, Box, Button, ButtonGroup, Grid, Paper, TextField, Typography } from "@mui/material";
import { DeviationId, createDeviation, updateDeviation, defDeviation } from "./deviationsApi";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useState } from "react";
import { docDeviationsSelectors, docTestsSelectors, selectDocument } from "../../documentSlice";
import { impossible } from "../../../../app/Utils";
import { showSuccess } from "../../../notifications/notificationsSlice";
import { LoadingButton } from "@mui/lab";
import { LoadingContainer } from "../../../../app/LoadingContainer";
import { LoadUsers } from "../../../../app/AppDataLoader";
import { selectLoggedUserId } from "../../../user/userSlice";
import { selectUserById } from "../../../users/usersSlice";
import { RequiredError } from "../../../../app/components/RequiredError";

interface DeviationFormProps {
    id: DeviationId | undefined
    testId?: number
    onSaved: (success: boolean) => void
    onCancel: () => void
}
export const CreateDeviationForm = (props: DeviationFormProps) => {
    const { id, testId } = props
    const dispatch = useAppDispatch()

    const deviation = useAppSelector(state => id ? docDeviationsSelectors.selectById(state, id) : undefined)
    const testName = useAppSelector(state => docTestsSelectors.selectById(state, testId ?? 0)?.testSpecification.name ?? '')
    const documentId = useAppSelector(state => selectDocument(state).doc?.header.id) ?? impossible()
    const userId = useAppSelector(selectLoggedUserId)
    const user = useAppSelector(st => selectUserById(st, userId))

    const [expectedResult, setExpectedResult] = useState(deviation?.expectedResult ?? '')
    const [realResult, setRealResult] = useState(deviation?.actualResult ?? '')
    const [showErr, setShowErr] = useState(false)

    const handleSaveClick = () => {
        setShowErr(true)
        if (expectedResult && realResult) {
            try {
                if (deviation) {
                    dispatch(updateDeviation({
                        ...deviation,
                        expectedResult,
                        actualResult: realResult,
                    })).unwrap()
                } else {
                    dispatch(createDeviation({
                        ...defDeviation,
                        expectedResult,
                        actualResult: realResult,
                        documentId: parseInt(documentId),
                        reporterUserName: user?.userName ?? null,
                        reporterFullName: `${user?.firstname ?? '?'} ${user?.surname ?? '?'}`,
                        reporterPosition: user?.position ?? '',
                        reporterCompany: user?.companyName ?? '',
                        reporterSignatureDate: new Date().toISOString(),
                        tests: testId ?  [{ documentTestId: testId, testName }] : [],
                    })).unwrap()
                }
                props.onSaved(true)
            } catch (error) {
                console.log(error)
            }
        }
    }

    return <LoadUsers component={<LoadingContainer />}>
        <Section title="Opis odchylenia">
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Oczekiwany rezultat"
                        multiline
                        minRows={3}
                        value={expectedResult}
                        onChange={e => setExpectedResult(e.target.value)}
                        error={showErr && !expectedResult}
                        helperText={showErr && !expectedResult && <RequiredError />}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Aktualny rezultat"
                        multiline
                        minRows={3}
                        value={realResult}
                        onChange={e => setRealResult(e.target.value)}
                        error={showErr && !realResult}
                        helperText={showErr && !realResult && <RequiredError />}
                    />
                </Grid>
            </Grid>
        </Section>
        <ButtonGroup>
            <Button variant='contained' onClick={handleSaveClick}>
                <Localized id='save'>Zapisz</Localized>
            </Button>
            <Button variant='outlined' onClick={props.onCancel}>
                <Localized id='close'>Zamknij</Localized>
            </Button>
        </ButtonGroup>
    </LoadUsers>
}

function Section(props: React.PropsWithChildren<{ title: string }>) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
                {props.title}
            </Typography>
            {props.children}
        </Box>
    );
}

export const AssociateDeviationWithTest = (props: DeviationFormProps) => {
    const { id, testId } = props
    const dispatch = useAppDispatch()

    const allDeviations = useAppSelector(state => docDeviationsSelectors.selectAll(state).filter(d => d.tests.findIndex(t => t.documentTestId === testId) < 0))


    const [deviation, setDeviation] = useState(allDeviations.length > 0 ? allDeviations[0] : null)
    const [saving, setSaving] = useState(false)

    const handleUserChange = (_, newDeviation) => {
        setDeviation(newDeviation ?? null)
    }

    const handleSaveClick = async () => {
        if (deviation && testId) {
            setSaving(true)
            try {
                await dispatch(updateDeviation({
                    ...deviation,
                    tests: [...deviation.tests, { documentTestId: testId, testName: '' }],
                })).unwrap()
                props.onSaved(true)
                dispatch(showSuccess('saved'))
            } catch (error) {
                console.log(error)
            } finally {
                setSaving(false)
            }
        }
    }

    return <Paper sx={{ mt: 1, p: 1 }}>
        <Grid sx={{ mb: 1 }} container spacing={2}>
            <Grid item xs={12} md={4}>
                <Autocomplete
                    sx={{
                        marginTop: 1,
                    }}
                    options={allDeviations}
                    value={deviation}
                    onChange={handleUserChange}
                    getOptionLabel={({ order }) => `Odchylenie nr ${order}`}
                    renderInput={params =>
                        <TextField {...params}
                            helperText={`Odchylenie nr ${deviation?.order ?? '...'}`}
                            label={'Odchylenie'}
                        />
                    }
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <Typography variant="h6">Oczekiwany rezultat</Typography>
                <Typography variant='body2'>{deviation?.expectedResult ?? ''}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
                <Typography variant="h6">Aktualny rezultat</Typography>
                <Typography variant='body2'>{deviation?.actualResult ?? ''}</Typography>
            </Grid>
       </Grid>
        <ButtonGroup>
            <LoadingButton loading={saving} variant='contained' onClick={handleSaveClick}>
                <>Skojarz</>
            </LoadingButton>
            <LoadingButton loading={saving} variant='outlined' onClick={props.onCancel}>
                <Localized id='cancel'>Anuluj</Localized>
            </LoadingButton>
        </ButtonGroup>
    </Paper>
}
